/* eslint-disable no-magic-numbers */
/* eslint-disable react/jsx-no-literals */
import React, { useState, useCallback, useEffect } from 'react';
import {
  IconBook2,
  IconTargetArrow,
  IconBulb,
  IconListDetails,
  IconCalendarEvent,
  IconArrowsSplit,
  IconClock,
  IconSparkles,
  IconPercentage,
  IconRewindBackward10,
  IconCalendarDue,
  IconCopy,
  IconCheck,
  IconExternalLink,
  IconCalendarWeek,
  IconCalendarMonth,
  IconCalendarStats
} from '@tabler/icons-react';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import { Sidebar } from '@futrli/ui-components';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import * as styles from './documentation-drawer.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface CopyBlockProps {
  code: string;
  index: number;
  copiedIndex: number | null;
  onCopy: (text: string, index: number) => void;
  copyAriaLabel: string;
}

const CopyBlock = ({ code, index, copiedIndex, onCopy, copyAriaLabel }: CopyBlockProps) => {
  const isCopied = copiedIndex === index;
  return (
    <div className={styles.docCopyBlock}>
      <span className={styles.docCopyCode}>{code}</span>
      <button
        type="button"
        className={isCopied ? `${styles.docCopyBtn} ${styles.docCopyBtnDone}` : styles.docCopyBtn}
        aria-label={copyAriaLabel}
        onClick={() => onCopy(code, index)}
      >
        {isCopied ? <IconCheck size={16} aria-hidden="true" /> : <IconCopy size={16} aria-hidden="true" />}
      </button>
    </div>
  );
};

const mono = (text: string) => <code className={styles.docInlineCode}>{text}</code>;
const italic = (chunks: React.ReactNode) => <em>{chunks}</em>;

const CODE_SNIPPETS = [
  '@Income * 0.1',
  '@Income LASTMONTH * 0.1',
  'IF(@Income > 50000, @Income * 0.05, @Income * 0.1)',
  'IF(MONTH() = 12, @Income LASTMONTH * 0.1, 0)',
  'IF( test , value if true , value if false )',
  'IF(MONTH()=3 || MONTH()=6 || MONTH()=9 || MONTH()=12, 500, 0)'
];

export const DocumentationDrawer = ({ isOpen, onClose }: Props) => {
  const intl = useIntl();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = useCallback((text: string, index: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1400);
    });
  }, []);

  const copyAriaLabel = intl.formatMessage({
    id: 'predict.doc_guide_copy_aria',
    defaultMessage: 'Copy formula'
  });

  // Carbon's Sidebar/Modal doesn't support closing on click outside, but it does render a
  // `[data-element="modal-background"]` overlay behind the sidebar - so we close the drawer when
  // that overlay is clicked, to mimic "click outside to close".
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target?.getAttribute('data-element') === 'modal-background') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [isOpen, onClose]);

  return (
    <div className={styles.docSidebarWrap}>
      <Sidebar
        open={isOpen}
        onCancel={onClose}
        size="large"
        header={
          <div className={styles.docDrawerTitle}>
            <IconBook2 size={22} aria-hidden="true" className={styles.docDrawerTitleIcon} />
            <FormattedMessage id="predict.writing_formulas" defaultMessage="Writing formulas" />
          </div>
        }
        className={styles.docSidebar}
      >
        <Form
          saveButton={
            <Button buttonType="primary" onClick={onClose}>
              <FormattedMessage id="common.ok" defaultMessage="OK" />
            </Button>
          }
          leftSideButtons={
            <a
              className={styles.docHelpLink}
              href="https://support.futrli.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="predict.doc_guide_help_link" defaultMessage="More in the Help Centre" />
              <IconExternalLink size={16} aria-hidden="true" />
            </a>
          }
          onSubmit={onClose}
        >
          {/* Oneliner callout */}
          <div className={styles.docOneliner}>
            <IconTargetArrow size={22} aria-hidden="true" className={styles.docOnelinerIcon} />
            <p className={styles.docOnelinerText}>
              <FormattedMessage
                id="predict.doc_guide_oneliner"
                defaultMessage="Formulas help you to reference other rows of financial data and perform mathematical operations on that data to generate a new prediction row."
              />
            </p>
          </div>

          {/* ── Section: Start with an example ── */}
          <section className={styles.docSection} id="basics">
            <h2 className={styles.docSectionHead}>
              <IconBulb size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage id="predict.doc_guide_section_examples" defaultMessage="Start with an example" />
            </h2>

            <div className={styles.docExample}>
              <p className={styles.docExCaption}>
                <FormattedMessage
                  id="predict.doc_guide_ex1_caption"
                  defaultMessage="An expense that is 10% of Income"
                />
              </p>
              <CopyBlock
                code={CODE_SNIPPETS[0]}
                index={0}
                copiedIndex={copiedIndex}
                onCopy={handleCopy}
                copyAriaLabel={copyAriaLabel}
              />
            </div>

            <div className={styles.docExample}>
              <p className={styles.docExCaption}>
                <FormattedMessage
                  id="predict.doc_guide_ex2_caption"
                  defaultMessage="10% of <em>last month's</em> Income"
                  values={{ em: italic }}
                />
              </p>
              <CopyBlock
                code={CODE_SNIPPETS[1]}
                index={1}
                copiedIndex={copiedIndex}
                onCopy={handleCopy}
                copyAriaLabel={copyAriaLabel}
              />
            </div>

            <div className={styles.docExample}>
              <p className={styles.docExCaption}>
                <FormattedMessage
                  id="predict.doc_guide_ex3_caption"
                  defaultMessage="10% of Income, dropping to 5% if Income tops 50,000 in a month"
                />
              </p>
              <CopyBlock
                code={CODE_SNIPPETS[2]}
                index={2}
                copiedIndex={copiedIndex}
                onCopy={handleCopy}
                copyAriaLabel={copyAriaLabel}
              />
            </div>

            <div className={styles.docExample}>
              <p className={styles.docExCaption}>
                <FormattedMessage
                  id="predict.doc_guide_ex4_caption"
                  defaultMessage="10% of last month's income, but only every December"
                />
              </p>
              <CopyBlock
                code={CODE_SNIPPETS[3]}
                index={3}
                copiedIndex={copiedIndex}
                onCopy={handleCopy}
                copyAriaLabel={copyAriaLabel}
              />
            </div>
          </section>

          {/* ── Section: The building blocks ── */}
          <section className={styles.docSection} id="blocks">
            <h2 className={styles.docSectionHead}>
              <IconListDetails size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage id="predict.doc_guide_section_building_blocks" defaultMessage="The building blocks" />
            </h2>
            <table className={styles.docTable}>
              <colgroup>
                <col style={{ width: '37%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '39%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.docTableTh}>
                    <FormattedMessage id="predict.doc_guide_table_you_want" defaultMessage="You want to..." />
                  </th>
                  <th className={styles.docTableTh}>
                    <FormattedMessage id="predict.doc_guide_table_use" defaultMessage="Use" />
                  </th>
                  <th className={styles.docTableTh}>
                    <FormattedMessage id="predict.doc_guide_table_example" defaultMessage="Example" />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage id="predict.doc_guide_table_row_reference" defaultMessage="Reference a row" />
                  </td>
                  <td className={styles.docTableTd}>{mono('@Name')}</td>
                  <td className={styles.docTableTd}>{mono('@Income')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage id="predict.doc_guide_table_row_maths" defaultMessage="Do maths" />
                  </td>
                  <td className={styles.docTableTd}>{mono('+ \u2212 * /')}</td>
                  <td className={styles.docTableTd}>{mono('@Income * 0.1')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage id="predict.doc_guide_table_row_lookback" defaultMessage="Look back a period" />
                  </td>
                  <td className={styles.docTableTd}>
                    {mono('LASTMONTH')}, {mono('LASTYEAR')}
                  </td>
                  <td className={styles.docTableTd}>{mono('@Income LASTMONTH')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage
                      id="predict.doc_guide_table_row_specific_date"
                      defaultMessage="Point at a specific date"
                    />
                  </td>
                  <td className={styles.docTableTd}>{mono('ON DATE(\u2026)')}</td>
                  <td className={styles.docTableTd}>{mono('@Income ON DATE(2020, 1)')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage id="predict.doc_guide_table_row_condition" defaultMessage="Add a condition" />
                  </td>
                  <td className={styles.docTableTd}>{mono('IF(\u2026)')}</td>
                  <td className={styles.docTableTd}>{mono('IF(@Income > 50000, 500, 0)')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage
                      id="predict.doc_guide_table_row_avg_or_total"
                      defaultMessage="Average or total a range"
                    />
                  </td>
                  <td className={styles.docTableTd}>{mono('AVG SUM MIN MAX')}</td>
                  <td className={styles.docTableTd}>{mono('AVG(@Income, DATE(0,-3), DATE(0,-1))')}</td>
                </tr>
                <tr>
                  <td className={styles.docTableTd}>
                    <FormattedMessage id="predict.doc_guide_table_row_check_date" defaultMessage="Check the date" />
                  </td>
                  <td className={styles.docTableTd}>
                    {mono('MONTH()')} {mono('YEAR()')}
                  </td>
                  <td className={styles.docTableTd}>{mono('IF(MONTH() = 12, 500, 0)')}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* ── Section: Reading dates ── */}
          <section className={styles.docSection} id="dates">
            <h2 className={styles.docSectionHead}>
              <IconCalendarEvent size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage id="predict.doc_guide_reading_dates" defaultMessage="Reading dates" />
            </h2>
            <p className={styles.functionExplain}>
              <FormattedMessage
                id="predict.doc_guide_date_offsets_intro_v2"
                defaultMessage="{DATE_FN} works as offsets from now:"
                values={{ DATE_FN: mono('DATE()') }}
              />
            </p>
            <div className={styles.docDates}>
              <div className={styles.docDatesRow}>
                <code className={styles.docInlineCode}>DATE(0, -2)</code>
                <span>
                  <FormattedMessage id="predict.doc_guide_date_two_months_ago_desc" defaultMessage="two months ago" />
                </span>
              </div>
              <div className={styles.docDatesRow}>
                <code className={styles.docInlineCode}>DATE(-1, -2)</code>
                <span>
                  <FormattedMessage
                    id="predict.doc_guide_date_one_year_two_months_ago_desc"
                    defaultMessage="one year and two months ago"
                  />
                </span>
              </div>
              <div className={styles.docDatesRow}>
                <code className={styles.docInlineCode}>DATE(2020, 1)</code>
                <span>
                  <FormattedMessage
                    id="predict.doc_guide_date_exact_desc"
                    defaultMessage="January 2020, an exact date"
                  />
                </span>
              </div>
            </div>
          </section>

          {/* ── Section: Adding conditions with IF ── */}
          <section className={styles.docSection} id="conditions">
            <h2 className={styles.docSectionHead}>
              <IconArrowsSplit size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage
                id="predict.doc_guide_section_conditions"
                defaultMessage="Adding conditions with {IF}"
                values={{ IF: mono('IF') }}
              />
            </h2>
            <CopyBlock
              code={CODE_SNIPPETS[4]}
              index={4}
              copiedIndex={copiedIndex}
              onCopy={handleCopy}
              copyAriaLabel={copyAriaLabel}
            />
            <p className={styles.functionExplain}>
              <FormattedMessage
                id="predict.doc_guide_if_compare_nest"
                defaultMessage="Compare with {gt} {lt} {gte} {lte} {eq}, and join tests with {and} (and) or {or} (or). You can nest an {IF} inside another to build tiers, like a commission rate that steps up as sales grow."
                values={{
                  gt: mono('>'),
                  lt: mono('<'),
                  gte: mono('>='),
                  lte: mono('<='),
                  eq: mono('='),
                  and: mono('&&'),
                  or: mono('||'),
                  IF: mono('IF')
                }}
              />
            </p>
            <div className={styles.docExample}>
              <p className={styles.docExCaption}>
                <FormattedMessage
                  id="predict.doc_guide_ex_quarterly_caption"
                  defaultMessage="A bonus at the end of each quarter"
                />
              </p>
              <CopyBlock
                code={CODE_SNIPPETS[5]}
                index={5}
                copiedIndex={copiedIndex}
                onCopy={handleCopy}
                copyAriaLabel={copyAriaLabel}
              />
            </div>
          </section>

          {/* ── Section: How often it runs ── */}
          <section className={styles.docSection} id="frequency">
            <h2 className={styles.docSectionHead}>
              <IconClock size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage id="predict.doc_guide_section_how_often" defaultMessage="How often it runs" />
            </h2>
            <p className={styles.functionExplain}>
              <FormattedMessage
                id="predict.doc_guide_frequency_basis_intro"
                defaultMessage="A reference like {INCOME_REF} means that day's, month's or year's value, depending on the basis you pick. Some tools only fit certain bases — {DAYOFMONTH} needs daily, {MONTH} needs daily or monthly."
                values={{
                  INCOME_REF: mono('@Income'),
                  DAYOFMONTH: mono('DAYOFMONTH()'),
                  MONTH: mono('MONTH()')
                }}
              />
            </p>
            <div className={styles.docFreqGrid}>
              <div className={styles.docFreqCard}>
                <IconCalendarWeek size={20} aria-hidden="true" className={styles.docFreqIcon} />
                <b>
                  <FormattedMessage id="predict.doc_guide_frequency_daily_label" defaultMessage="Daily" />
                </b>
                <span className={styles.docFreqDesc}>
                  <FormattedMessage id="predict.doc_guide_frequency_daily_desc" defaultMessage="One number per day" />
                </span>
              </div>
              <div className={`${styles.docFreqCard} ${styles.docFreqCardPick}`}>
                <IconCalendarMonth size={20} aria-hidden="true" className={styles.docFreqIcon} />
                <b>
                  <FormattedMessage id="predict.doc_guide_frequency_monthly_label" defaultMessage="Monthly" />
                </b>
                <span className={styles.docFreqDesc}>
                  <FormattedMessage
                    id="predict.doc_guide_frequency_monthly_desc"
                    defaultMessage="The most common choice, one number per month"
                  />
                </span>
                <span className={styles.docFreqTag}>
                  <FormattedMessage id="predict.doc_guide_frequency_recommended" defaultMessage="Recommended" />
                </span>
              </div>
              <div className={styles.docFreqCard}>
                <IconCalendarStats size={20} aria-hidden="true" className={styles.docFreqIcon} />
                <b>
                  <FormattedMessage id="predict.doc_guide_frequency_annually_label" defaultMessage="Annually" />
                </b>
                <span className={styles.docFreqDesc}>
                  <FormattedMessage
                    id="predict.doc_guide_frequency_annually_desc"
                    defaultMessage="One number per year"
                  />
                </span>
              </div>
            </div>
          </section>

          {/* ── Section: Handy tips ── */}
          <section className={styles.docSection} id="tips">
            <h2 className={styles.docSectionHead}>
              <IconSparkles size={19} aria-hidden="true" className={styles.docSectionIcon} />
              <FormattedMessage id="predict.doc_guide_section_tips" defaultMessage="Handy tips" />
            </h2>
            <div className={styles.docTips}>
              <div className={styles.docTip}>
                <IconPercentage size={18} aria-hidden="true" className={styles.docTipIcon} />
                <span>
                  <FormattedMessage
                    id="predict.doc_guide_tip_percentages_v2"
                    defaultMessage="Percentages become decimals — 10% → {p10}, 5% → {p5}, 0.5% → {p05}."
                    values={{
                      p10: mono('0.1'),
                      p5: mono('0.05'),
                      p05: mono('0.005')
                    }}
                  />
                </span>
              </div>
              <div className={styles.docTip}>
                <IconRewindBackward10 size={18} aria-hidden="true" className={styles.docTipIcon} />
                <span>
                  <FormattedMessage
                    id="predict.doc_guide_tip_lastmonth_v2"
                    defaultMessage="Reach for {LASTMONTH} / {LASTYEAR} first; only use {ON_DATE} to go further back or name an exact date."
                    values={{
                      LASTMONTH: mono('LASTMONTH'),
                      LASTYEAR: mono('LASTYEAR'),
                      ON_DATE: mono('ON DATE(\u2026)')
                    }}
                  />
                </span>
              </div>
              <div className={styles.docTip}>
                <IconCalendarDue size={18} aria-hidden="true" className={styles.docTipIcon} />
                <span>
                  <FormattedMessage
                    id="predict.doc_guide_tip_frequency_note"
                    defaultMessage="Setting to daily produces a number per day, monthly produces 1 number per month and annually produces 1 number per year."
                  />
                </span>
              </div>
            </div>
          </section>
        </Form>
      </Sidebar>
    </div>
  );
};
